import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Lightning, Trophy, ChartBar, Target } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { ToolCTA, InlineNewsletter, ScoreCTA } from "@/components/InlineCTA";
import SEO from "@/components/SEO";
import PublicNav from "@/components/PublicNav";
const API = process.env.REACT_APP_BACKEND_URL;
const PC = { QB: "#ff6b6b", RB: "#4ecdc4", WR: "#45b7d1", TE: "#f9ca24" };

const CHAMP_DATA = {
  total: 6976, sf: 6324, oneqb: 652,
  pct_top10_rb: 82.5, pct_top10_wr: 74.4, pct_top5_qb_sf: 39.8, pct_top10_qb_sf: 65.8,
  pct_top5_te: 44.3, avg_wr_top30: 2.7, top3_concentration: 36.5,
  avg_age: 27.4,
  alloc: { WR: 35.0, RB: 30.2, QB: 22.5, TE: 12.3 },
};

const allocChart = [
  { pos: "WR", all: 35.0, sf: 34.9, oneqb: 36.5 },
  { pos: "RB", all: 30.2, sf: 29.6, oneqb: 29.8 },
  { pos: "QB", all: 22.5, sf: 23.4, oneqb: 21.4 },
  { pos: "TE", all: 12.3, sf: 12.1, oneqb: 12.4 },
];

function IB({ label, pct, color }) {
  return (
    <div className="mb-2">
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-xs text-[#a3a3a3]">{label}</span>
        <span className="text-xs font-mono font-bold text-white">{pct}%</span>
      </div>
      <div className="h-4 bg-[#1a1a1a] rounded-sm overflow-hidden">
        <div className="h-full rounded-sm" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

function ADPTable({ players, title, subtitle, limit = 10 }) {
  if (!players || !players.length) return null;
  return (
    <div className="border border-[#262626] bg-[#171717] my-4 overflow-hidden" data-testid="adp-table">
      <div className="p-4 border-b border-[#262626]">
        <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#ccff00] flex items-center gap-1.5"><ChartBar size={14} weight="fill" /> {title}</h3>
        {subtitle && <p className="text-[10px] text-[#555] mt-1">{subtitle}</p>}
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[10px] text-[#666] uppercase tracking-wider border-b border-[#262626]">
            <th className="text-left py-2 px-4">Pick</th>
            <th className="text-left py-2 px-4">Player</th>
            <th className="text-left py-2 px-4">Pos</th>
            <th className="text-right py-2 px-4">ADP</th>
            <th className="text-right py-2 px-4 hidden sm:table-cell">Range</th>
          </tr>
        </thead>
        <tbody>
          {players.slice(0, limit).map((p, i) => (
            <tr key={i} className="border-b border-[#1a1a1a] hover:bg-[#1a1a1a]/50">
              <td className="py-2 px-4 text-[#555] font-mono">{i + 1}</td>
              <td className="py-2 px-4 text-white font-medium">{p.name}</td>
              <td className="py-2 px-4">
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm" style={{ color: PC[p.position] || "#888", backgroundColor: `${PC[p.position] || "#888"}15` }}>{p.position}</span>
              </td>
              <td className="py-2 px-4 text-right font-mono text-[#a3a3a3]">{p.adp}</td>
              <td className="py-2 px-4 text-right font-mono text-[#555] hidden sm:table-cell">{p.highest}–{p.lowest}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RoundDistChart({ data }) {
  if (!data || !data.length) return null;
  const chartData = data.slice(0, 12).map(r => ({
    round: `R${r.round}`,
    QB: r.QB_pct,
    RB: r.RB_pct,
    WR: r.WR_pct,
    TE: r.TE_pct,
  }));
  return (
    <div className="border border-[#262626] bg-[#171717] p-5 my-4">
      <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-3">POSITIONAL DISTRIBUTION BY ROUND (STARTUP DRAFTS)</h3>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={chartData} margin={{ left: 0, right: 0 }}>
          <XAxis dataKey="round" tick={{ fontSize: 10, fill: "#666" }} />
          <YAxis tick={{ fontSize: 10, fill: "#666" }} tickFormatter={v => `${v}%`} />
          <Tooltip contentStyle={{ background: "#171717", border: "1px solid #262626", fontSize: 11 }} formatter={v => `${v}%`} />
          <Bar dataKey="QB" stackId="a" fill={PC.QB} />
          <Bar dataKey="RB" stackId="a" fill={PC.RB} />
          <Bar dataKey="WR" stackId="a" fill={PC.WR} />
          <Bar dataKey="TE" stackId="a" fill={PC.TE} />
        </BarChart>
      </ResponsiveContainer>
      <div className="flex items-center gap-4 mt-2 justify-center">
        {Object.entries(PC).map(([pos, color]) => (
          <span key={pos} className="flex items-center gap-1 text-[10px] text-[#888]">
            <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ backgroundColor: color }} />
            {pos}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function StartupDraftDataGuide() {
  const [draftData, setDraftData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/drafts/analysis`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { setDraftData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const startup = draftData?.startup;
  const rookie = draftData?.rookie;
  const rd1 = startup?.round_distribution?.find(r => r.round === 1);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO title="Dynasty Startup Draft Guide (2026): What Actually Wins — Data From 6,976 Champions — Dynasty Trade Maker" description="The data-backed dynasty startup draft guide. We analyzed 6,976 championship rosters and 800+ startup drafts to show what positions, strategies, and roster shapes actually win." path="/guide/dynasty-startup-draft" image="https://dynastytrademaker.com/hero-images/guide-startup-draft.jpg" />
      <PublicNav />
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Link to="/guide" className="text-[10px] text-[#555] hover:text-[#a3a3a3] no-underline uppercase tracking-wider">Guide</Link>
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#ccff00] block mt-4 mb-2">DATA FROM {CHAMP_DATA.total.toLocaleString()} CHAMPIONS{startup ? ` + ${startup.total_drafts.toLocaleString()} STARTUP DRAFTS` : ""}</span>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-white uppercase mb-4" data-testid="guide-title">THE DYNASTY STARTUP DRAFT GUIDE: <span className="text-[#ccff00]">WHAT ACTUALLY WINS</span></h1>

        {/* Hero Image */}
        <div className="mb-6 border border-[#262626] rounded-sm overflow-hidden">
          <img src="/hero-images/guide-startup-draft.jpg" alt="" className="w-full h-auto" loading="eager" />
        </div>

        <div className="text-sm text-[#a3a3a3] leading-relaxed space-y-6">
          <p>A startup draft is the most important day of your dynasty league. You're not setting a lineup for one season. You're laying the foundation for a team you'll own for years. Get it right and you're a contender before the season even starts. Get it wrong and you're digging out of a hole for two or three years.</p>
          <p>Most startup advice is somebody's opinion about who to pick. This guide is different. We pulled real championship rosters from {CHAMP_DATA.total.toLocaleString()} Sleeper dynasty leagues{startup ? ` and analyzed ${startup.total_drafts.toLocaleString()} actual startup drafts from those same champion leagues` : ""} to answer one question: not who the experts like, but <strong className="text-white">what actually won</strong>.</p>

          {/* Real ADP Data Section */}
          {startup && (
            <>
              <h2 className="font-heading text-2xl text-white uppercase mt-10 mb-3">REAL STARTUP ADP: HOW CHAMPION LEAGUES DRAFTED</h2>
              <p>These aren't mock-draft projections. This is how {startup.total_drafts.toLocaleString()} real dynasty leagues that produced champions actually drafted. The average draft position (ADP) tells you where the market puts each player — use it to spot value and avoid reaching.</p>

              {rd1 && (
                <div className="border border-[#262626] bg-[#171717] p-5 my-4">
                  <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#ccff00] mb-3 flex items-center gap-1.5"><Target size={14} weight="fill" /> ROUND 1 BY POSITION</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { pos: "QB", pct: rd1.QB_pct, label: "dominated round 1" },
                      { pos: "WR", pct: rd1.WR_pct, label: "second most picked" },
                      { pos: "RB", pct: rd1.RB_pct, label: "fell after QBs/WRs" },
                      { pos: "TE", pct: rd1.TE_pct, label: "Bowers effect" },
                    ].map(({ pos, pct, label }) => (
                      <div key={pos} className="text-center">
                        <div className="text-2xl font-bold font-mono" style={{ color: PC[pos] }}>{pct}%</div>
                        <div className="text-[10px] font-bold text-white">{pos}</div>
                        <div className="text-[10px] text-[#555]">{label}</div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-[#555] mt-3">Based on {rd1.total.toLocaleString()} round-1 picks across {startup.total_drafts.toLocaleString()} startup drafts.</p>
                </div>
              )}

              <ADPTable players={startup.top_50_adp} title="STARTUP ADP TOP 25" subtitle={`Average draft position across ${startup.total_drafts.toLocaleString()} real startup drafts from champion leagues.`} limit={25} />

              <RoundDistChart data={startup.round_distribution} />
            </>
          )}

          {/* Championship Proof */}
          <h2 className="font-heading text-2xl text-white uppercase mt-10 mb-3">WHAT CHAMPIONSHIP ROSTERS LOOK LIKE</h2>
          <p>Before strategy, look at the evidence. We analyzed {CHAMP_DATA.total.toLocaleString()} dynasty league champions ({CHAMP_DATA.sf.toLocaleString()} Superflex, {CHAMP_DATA.oneqb.toLocaleString()} 1QB) to find out what winning rosters have in common.</p>

          <div className="border border-[#262626] bg-[#171717] p-5 my-4">
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#ccff00] mb-3 flex items-center gap-1.5"><Trophy size={14} weight="fill" /> WHAT CHAMPIONS HAD</h3>
            <IB label="Had a top-10 dynasty RB" pct={CHAMP_DATA.pct_top10_rb} color={PC.RB} />
            <IB label="Had a top-10 dynasty WR" pct={CHAMP_DATA.pct_top10_wr} color={PC.WR} />
            <IB label="Had a top-10 dynasty QB" pct={65.8} color={PC.QB} />
            <IB label="Had a top-5 dynasty TE" pct={CHAMP_DATA.pct_top5_te} color={PC.TE} />
            <p className="text-[10px] text-[#555] mt-3">Source: {CHAMP_DATA.total.toLocaleString()} Sleeper dynasty league champions, 2025 season.</p>
          </div>

          <p>The headline: <strong className="text-white">{CHAMP_DATA.pct_top10_rb}% of champions had a top-10 dynasty running back</strong>. That's the highest correlation at any position. Wide receiver is close at {CHAMP_DATA.pct_top10_wr}%. Elite skill players win — this is not the place to get cute or chase value.</p>
          <p>One nuance: winners didn't lean on a single star receiver. They averaged <strong className="text-white">{CHAMP_DATA.avg_wr_top30} top-30 WRs</strong> per roster. A "multiple good WRs" approach shows up on winning teams more than putting all your eggs in one receiver.</p>

          {/* Round by Round with real data */}
          <h2 className="font-heading text-2xl text-white uppercase mt-10 mb-3">ROUND BY ROUND: HOW TO THINK</h2>

          <h3 className="font-heading text-lg text-white uppercase mt-6 mb-2">THE EARLY ROUNDS (YOUR FOUNDATION)</h3>
          <p>These picks are your franchise cornerstones. The champion data is clear: <strong className="text-white">lock up elite talent early</strong>. {CHAMP_DATA.pct_top10_rb}% of champions had a top-10 RB and {CHAMP_DATA.pct_top10_wr}% had a top-10 WR.
          {startup && rd1 && <> In real startup drafts, QBs command <strong className="text-white">{rd1.QB_pct}%</strong> of round 1 picks — the Superflex era has made elite QBs the most expensive assets in the game.</>}
          </p>

          <h3 className="font-heading text-lg text-white uppercase mt-6 mb-2">THE MIDDLE ROUNDS (WHERE LEAGUES ARE WON)</h3>
          <p>Everyone gets the early picks roughly right. The middle rounds are where sharp drafters separate. This is where you balance your build: filling out that receiver room, deciding how you're handling quarterback, and grabbing young players with room to grow.</p>
          {startup?.round_distribution && (() => {
            const r5 = startup.round_distribution.find(r => r.round === 5);
            const r8 = startup.round_distribution.find(r => r.round === 8);
            if (!r5 || !r8) return null;
            return <p className="text-xs text-[#888]">In rounds 5–8, WR picks rise to {r5.WR_pct}–{r8.WR_pct}% as managers fill depth. RB picks hold steady around {r5.RB_pct}–{r8.RB_pct}%.</p>;
          })()}

          <h3 className="font-heading text-lg text-white uppercase mt-6 mb-2">THE LATE ROUNDS (SWING FOR UPSIDE)</h3>
          <p>Late in a startup, safe veterans are close to worthless in dynasty. Their value only goes down. Bet on youth: rookies, second-year breakout candidates, backups a snap away from a role.</p>

          {/* QB Question */}
          <h2 className="font-heading text-2xl text-white uppercase mt-10 mb-3">THE QUARTERBACK QUESTION</h2>

          <ToolCTA text="Wondering what your QB is worth in a trade?" subtext="Check real-time Superflex and 1QB values for any player." buttonText="CHECK QB VALUES" buttonLink="/trade-calculator" />
          <p>How you handle QB depends entirely on your format, and it's the single biggest strategic fork in a startup.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-4">
            <div className="border border-[#262626] bg-[#171717] p-4">
              <h4 className="text-[10px] font-bold uppercase text-[#ccff00] mb-2">SUPERFLEX</h4>
              <IB label="Champs with top-5 QB" pct={CHAMP_DATA.pct_top5_qb_sf} color={PC.QB} />
              <IB label="Champs with top-10 QB" pct={CHAMP_DATA.pct_top10_qb_sf} color={PC.QB} />
              {rd1 && <p className="text-xs text-[#888] mt-2">In real drafts, {rd1.QB_pct}% of first-round picks are QBs. You need two starters you trust.</p>}
            </div>
            <div className="border border-[#262626] bg-[#171717] p-4">
              <h4 className="text-[10px] font-bold uppercase text-[#ccff00] mb-2">1QB</h4>
              <p className="text-xs text-[#888]">QB is streamable. Plenty of 1QB champions waited on quarterback and won anyway. Pour those early picks into running backs and receivers instead.</p>
            </div>
          </div>

          <p>Know which game you're playing before you draft. <Link to="/research/qb-question" className="text-[#ccff00] no-underline hover:underline">Full QB analysis →</Link></p>

          {/* Blueprint */}
          <h2 className="font-heading text-2xl text-white uppercase mt-10 mb-3">THE CHAMPION ROSTER BLUEPRINT</h2>
          <p>Champions concentrated real value in their best players. On title rosters, the <strong className="text-white">top 3 players held {CHAMP_DATA.top3_concentration}%</strong> of the roster's total value.</p>

          <div className="border border-[#262626] bg-[#171717] p-5 my-4">
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-3">HOW CHAMPIONS ALLOCATE VALUE</h3>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={allocChart} layout="vertical" margin={{ left: 30 }}>
                <XAxis type="number" tick={{ fontSize: 10, fill: "#666" }} domain={[0, 40]} tickFormatter={v => `${v}%`} />
                <YAxis type="category" dataKey="pos" tick={{ fontSize: 12, fill: "#a3a3a3" }} width={28} />
                <Tooltip contentStyle={{ background: "#171717", border: "1px solid #262626", fontSize: 12 }} formatter={v => `${v}%`} />
                <Bar dataKey="all" radius={[0, 3, 3, 0]}>{allocChart.map((e, i) => <Cell key={i} fill={PC[e.pos]} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
            <p className="text-[10px] text-[#555] mt-2">Value allocation on {CHAMP_DATA.total.toLocaleString()} championship rosters. <Link to="/research/positional-blueprint" className="text-[#ccff00] no-underline hover:underline">Full breakdown →</Link></p>
          </div>

          <p>Use that as a compass for how to distribute your picks. WR gets the biggest share ({CHAMP_DATA.alloc.WR}%), followed by RB ({CHAMP_DATA.alloc.RB}%), then QB ({CHAMP_DATA.alloc.QB}%) and TE ({CHAMP_DATA.alloc.TE}%).</p>

          {/* Rookie ADP */}
          {rookie && rookie.top_36_adp?.length > 0 && (
            <>
              <h2 className="font-heading text-2xl text-white uppercase mt-10 mb-3">2025 ROOKIE DRAFT ADP</h2>
              <p>If you're trading rookie picks during your startup, here's what those picks are actually buying. This is ADP from {rookie.total_drafts.toLocaleString()} real rookie drafts in champion leagues.</p>
              <ADPTable players={rookie.top_36_adp} title="ROOKIE ADP TOP 24" subtitle={`From ${rookie.total_drafts.toLocaleString()} real rookie drafts.`} limit={24} />
            </>
          )}

          {/* Age */}
          <h2 className="font-heading text-2xl text-white uppercase mt-10 mb-3">DON'T SLEEP ON AGE (BUT DON'T WORSHIP IT)</h2>
          <p>Dynasty managers panic about age, and the data says they overdo it. The average championship roster was <strong className="text-white">{CHAMP_DATA.avg_age} years old</strong>. Winners rostered plenty of "old" players because production beats a birthday during your contention window.</p>
          <p>Match your age profile to your timeline, not to a fear of getting older. <Link to="/research/age-of-champion" className="text-[#ccff00] no-underline hover:underline">Full age analysis →</Link></p>

          {/* Mistakes */}
          <h2 className="font-heading text-2xl text-white uppercase mt-10 mb-3">COMMON STARTUP MISTAKES</h2>

          <InlineNewsletter context="weekly" source="startup-guide" />
          <div className="space-y-3 my-4">
            {[
              { title: "Reaching for a QB too early in 1QB", desc: "You're spending a premium pick on a position that isn't scarce in single-QB formats." },
              { title: "Drafting old in the late rounds", desc: "Safe veterans late are dead weight in dynasty. Bet on youth — the downside is tiny, the upside is a multi-year asset." },
              { title: "Ignoring your window", desc: "If you're building young, don't panic-draft win-now vets. If you're loading up to win, don't waste picks on projects." },
              { title: "Spreading too thin", desc: `Champions concentrated ${CHAMP_DATA.top3_concentration}% of value in their top 3 players. A roster of solid-but-unspectacular players wins nothing.` },
            ].map(m => (
              <div key={m.title} className="border border-[#262626] bg-[#171717] p-4">
                <p className="text-sm text-white font-semibold mb-1">{m.title}</p>
                <p className="text-xs text-[#888]">{m.desc}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="border border-[#ccff00]/30 bg-[#ccff00]/5 p-6 text-center my-8" data-testid="guide-cta">
            <h3 className="font-heading text-lg text-white uppercase mb-2">DRAFT WITH DATA, NOT NOISE</h3>
            <p className="text-xs text-[#888] mb-4">Run your picks and potential trades through our tools so you're never guessing at value on the clock.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/trade-calculator" className="h-9 px-5 bg-[#ccff00] text-black rounded-sm text-[11px] inline-flex items-center gap-1.5 no-underline font-bold hover:bg-[#b8e600] transition-colors" data-testid="guide-cta-calculator"><Lightning size={12} weight="fill" /> CHECK ANY VALUE, FREE</Link>
              <Link to="/championship-blueprint" className="text-[11px] text-[#ccff00] no-underline hover:underline flex items-center gap-1">See champion roster data <ArrowRight size={11} /></Link>
            </div>
          </div>

          {/* FAQ */}
          <h2 className="font-heading text-2xl text-white uppercase mt-10 mb-3">FREQUENTLY ASKED QUESTIONS</h2>
          <div className="space-y-3" itemScope itemType="https://schema.org/FAQPage">
            {[
              { q: "How is a dynasty startup draft different from a redraft draft?", a: "In dynasty you keep your players every year, so you draft for a multi-year window, not one season. Young players and long-term value matter far more than in redraft." },
              { q: "Should I draft a quarterback early in my startup?", a: `It depends on format. In Superflex, yes — ${CHAMP_DATA.pct_top5_qb_sf}% of SF champions had a top-5 QB${rd1 ? `, and ${rd1.QB_pct}% of real round-1 startup picks are QBs` : ""}. In 1QB you can wait.` },
              { q: "What's the best startup draft strategy?", a: `The data points to concentrating value in a strong core of elite skill players (${CHAMP_DATA.pct_top10_rb}% of champs had a top-10 RB), handling QB correctly for your format, and betting on youth late.` },
              { q: "How young should my dynasty roster be?", a: `Younger isn't automatically better. The average championship roster was ${CHAMP_DATA.avg_age} years old. Draft players who'll be productive during your contention window.` },
              { q: "How many elite players do I need?", a: `Champions concentrated value — top 3 players held ${CHAMP_DATA.top3_concentration}% of total roster value on average. You want a clear core of high-end talent.` },
            ].map((f, i) => (
              <div key={i} className="border border-[#262626] bg-[#171717] p-4" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <p className="text-sm text-white font-semibold mb-1" itemProp="name">{f.q}</p>
                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p className="text-xs text-[#888]" itemProp="text">{f.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Related */}
        <div className="mt-8 space-y-2">
          <p className="text-[9px] font-bold uppercase tracking-wider text-[#555]">RELATED RESEARCH</p>
          {[
            { href: "/tools/draft-archetype", label: "Draft Archetype Finder", desc: "Which champion build matches yours?" },
            { href: "/research/championship-report", label: "Full Championship Report", desc: "Every finding from 6,976 champions" },
            { href: "/research/qb-question", label: "The QB Question", desc: "Do you need a top-5 QB?" },
            { href: "/research/positional-blueprint", label: "Positional Blueprint", desc: "How champions allocate value" },
            { href: "/guide/superflex-strategy", label: "Superflex Strategy Guide", desc: "Live QB value comparisons" },
          ].map(l => (
            <Link key={l.href} to={l.href} className="flex items-center justify-between p-3 border border-[#262626] bg-[#171717] hover:border-[#ccff00]/30 no-underline rounded-sm transition-colors">
              <div><p className="text-xs text-white font-semibold">{l.label}</p><p className="text-[10px] text-[#666]">{l.desc}</p></div>
              <ArrowRight size={12} className="text-[#555]" />
            </Link>
          ))}
        </div>

        <div className="mt-6 text-center text-[10px] text-[#444] space-x-3">
          <Link to="/guide" className="hover:text-[#a3a3a3] no-underline">Guides</Link>
          <Link to="/rankings" className="hover:text-[#a3a3a3] no-underline">Rankings</Link>
          <Link to="/trade-calculator" className="hover:text-[#a3a3a3] no-underline">Calculator</Link>
        </div>
      </div>
    </div>
  );
}
